import { Download, Edit, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { ContractDetailsSection } from "@/features/contracts/components/contract-details-section"
import { ContractOrdersTable } from "@/features/contracts/components/contract-orders-table"
import { ContractStatusBadge } from "@/features/contracts/components/contract-status-badge"
import { ContractSummaryCards } from "@/features/contracts/components/contract-summary-cards"
import { useDeleteContract } from "@/features/contracts/hooks/use-contract-mutations"
import { useContract, contractsKeys } from "@/features/contracts/hooks/use-contracts"
import { exportContractToCsv } from "@/features/contracts/lib/contract-export"
import { DocumentsList } from "@/features/documents/components/documents-list"
import { paths } from "@/routes/paths"

export function ContractDetailPage() {
  const { contractId = "" } = useParams<{ contractId: string }>()
  const { t } = useTranslation(["contracts", "common"])
  const navigate = useNavigate()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: contract, isLoading } = useContract(contractId)
  const remove = useDeleteContract()

  if (isLoading || !contract) {
    return <LoadingState />
  }

  return (
    <div className="flex flex-col gap-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("title"), to: paths.contracts },
          { label: contract.contractNumber ?? `#${contract.id}` },
        ]}
      />

      <PageHeader
        title={contract.contractNumber ?? `#${contract.id}`}
        description={`${contract.clientName ?? ""}${
          contract.productType
            ? ` · ${t(`options.productTypes.${contract.productType}`, { defaultValue: contract.productType })}`
            : ""
        }`}
        meta={<ContractStatusBadge status={contract.status} />}
        actions={
          <>
            <Button asChild>
              <Link to={paths.contractOrderNew(contract.id)}>
                <Plus className="size-4" />
                {t("orders.addOrder")}
              </Link>
            </Button>
            <Button variant="outline" onClick={() => exportContractToCsv(contract)}>
              <Download className="size-4" />
              {t("actions.export", { ns: "common" })}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(paths.contractEdit(contract.id))}
            >
              <Edit className="size-4" />
              {t("actions.edit", { ns: "common" })}
            </Button>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="size-4" />
              {t("actions.delete", { ns: "common" })}
            </Button>
          </>
        }
      />

      <ContractSummaryCards contract={contract} />
      <ContractDetailsSection contract={contract} />

      <DocumentsList
        documents={contract.documents}
        level="contract"
        contractId={String(contract.id)}
        invalidateKeys={[contractsKeys.detail(String(contract.id))]}
        title={t("documents.title")}
      />

      <ContractOrdersTable contract={contract} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteContract")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t("actions.cancel", { ns: "common" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                remove.mutate(String(contract.id), {
                  onSuccess: () => {
                    setDeleteOpen(false)
                    navigate(paths.contracts, { replace: true })
                  },
                })
              }}
              disabled={remove.isPending}
            >
              {remove.isPending
                ? t("actions.deleting", { ns: "common" })
                : t("actions.delete", { ns: "common" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
